import React, { useEffect, useMemo, useRef, useState } from 'react';
import { allocateIncomingScans } from '../../../../utils/incomingScanAllocation';
import { parseInventoryMoney, parseInventoryQuantity } from '../../../../utils/inventoryInputs';
import { adminProductApi } from '../../../../api/adminProductApi';
import {
  expectedItemHasValidPurchasePrice,
  expectedItemStatusBadgeClass,
  expectedItemStatusLabel,
  EXPECTED_ITEM_STATUS_SELECT_ORDER
} from './incomingShipmentExpectedStatusUi';
import IncomingStatusBadgePicker from './IncomingStatusBadgePicker';
import IncomingItemMargin from './IncomingItemMargin';

const fmtPrice = (p) => {
  if (p == null || p === '') return '—';
  const n = Number(p);
  if (Number.isNaN(n)) return String(p);
  return n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtMoneyUah = (n) => {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' грн';
};

const productLine = (p) => {
  if (!p) return '';
  const code = p.code || p.productCode || (p.id != null ? `#${p.id}` : '');
  return `${code} — ${p.name || ''}`.trim();
};

const normalizeBarcodeField = (v) => {
  if (v == null || v === '') return null;
  const t = String(v).trim();
  return t === '' ? null : t;
};

/**
 * Згортається блок «Очікувані товари» + таблиця + додавання з пошуку по каталогу.
 */
const IncomingShipmentExpectedBlock = ({
  items,
  scannedItems,
  onAddItem,
  onDeleteItem,
  adding,
  deletingId,
  onOpenExcelImport,
  onSaveExpectedBarcode,
  savingBarcodeId,
  onBindExpectedItem,
  bindingExpectedId,
  onChangeExpectedItemStatus,
  savingExpectedStatusId,
  onPostExpectedToSite,
  postingExpectedToSiteId,
  onSaveExpectedPurchasePrice,
  savingPurchasePriceId,
  /** Блокує кнопку «з просканованих», коли йде внесення / завантаження тощо */
  incomingToolbarBusy,
  onGenerateExpectedFromScanned,
  generatingExpectedFromScanned,
  /** Поки відкрита модалка підтвердження видалення — усі кнопки видалення вимкнені */
  expectedDeleteConfirmOpen
}) => {
  const expectedStatusOptionsFor = (current) => {
    const cur = current || 'pending';
    const set = new Set(EXPECTED_ITEM_STATUS_SELECT_ORDER);
    const list = [...EXPECTED_ITEM_STATUS_SELECT_ORDER];
    if (cur && !set.has(cur)) list.unshift(cur);
    return list;
  };
  const [open, setOpen] = useState(true);
  const [showPurchasePriceColumn, setShowPurchasePriceColumn] = useState(true);
  const columnCount = showPurchasePriceColumn ? 9 : 7;
  const [manualAddOpen, setManualAddOpen] = useState(false);
  const [editingBarcodeId, setEditingBarcodeId] = useState(null);
  const [editingBarcodeValue, setEditingBarcodeValue] = useState('');
  const barcodeInputRef = useRef(null);
  const bindInputRef = useRef(null);
  const [productQuery, setProductQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState('1');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [manualInputError, setManualInputError] = useState('');
  const [bindOpenItemId, setBindOpenItemId] = useState(null);
  const [bindQuery, setBindQuery] = useState('');
  const [bindResults, setBindResults] = useState([]);
  const [bindLoading, setBindLoading] = useState(false);
  const [editingPurchasePriceId, setEditingPurchasePriceId] = useState(null);
  const [editingPurchasePriceValue, setEditingPurchasePriceValue] = useState('');
  const purchasePriceInputRef = useRef(null);

  const totalPurchaseSum = useMemo(() => {
    let sum = 0;
    for (const it of (items || [])) {
      if (!it) continue;
      const qty = Math.max(0, parseInt(it.quantity, 10) || 0);
      if (!qty) continue;
      const price = Number(it.purchasePrice);
      if (!Number.isFinite(price)) continue;
      sum += price * qty;
    }
    return sum;
  }, [items]);

  const scanAllocation = useMemo(() => allocateIncomingScans(items || [], scannedItems || []), [items, scannedItems]);
  const getScannedQtyForExpected = item => scanAllocation.allocated.get(item.id) || 0;

  const showGenerateFromScannedBtn = useMemo(() => {
    if (!onGenerateExpectedFromScanned) return false;
    return (items || []).length === 0 && (scannedItems || []).length > 0;
  }, [onGenerateExpectedFromScanned, items, scannedItems]);

  useEffect(() => {
    if (selectedProduct) {
      setSearchResults([]);
      return;
    }
    const q = (productQuery || '').trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    let active = true;
    const timer = setTimeout(() => {
      adminProductApi
        .searchProductsInfo(q)
        .then((res) => { if (active) setSearchResults(res.data?.data || []); })
        .catch(() => { if (active) setSearchResults([]); })
        .finally(() => { if (active) setSearchLoading(false); });
    }, 220);
    return () => { active = false; clearTimeout(timer); };
  }, [productQuery, selectedProduct]);

  useEffect(() => {
    if (bindOpenItemId == null) {
      setBindQuery('');
      setBindResults([]);
      setBindLoading(false);
      return;
    }
    const q = (bindQuery || '').trim();
    if (q.length < 2) {
      setBindResults([]);
      setBindLoading(false);
      return;
    }
    setBindLoading(true);
    let active = true;
    const timer = setTimeout(() => {
      adminProductApi
        .searchProductsInfo(q)
        .then((res) => { if (active) setBindResults(res.data?.data || []); })
        .catch(() => { if (active) setBindResults([]); })
        .finally(() => { if (active) setBindLoading(false); });
    }, 220);
    return () => { active = false; clearTimeout(timer); };
  }, [bindQuery, bindOpenItemId]);

  useEffect(() => {
    if (editingBarcodeId == null) return;
    const t = window.setTimeout(() => {
      const el = barcodeInputRef.current;
      if (el) {
        el.focus();
        el.select();
      }
    }, 0);
    return () => window.clearTimeout(t);
  }, [editingBarcodeId]);

  useEffect(() => {
    if (bindOpenItemId == null) return;
    const t = window.setTimeout(() => {
      const el = bindInputRef.current;
      if (el) el.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [bindOpenItemId]);

  useEffect(() => {
    if (editingPurchasePriceId == null) return;
    const t = window.setTimeout(() => {
      const el = purchasePriceInputRef.current;
      if (el) {
        el.focus();
        el.select();
      }
    }, 0);
    return () => window.clearTimeout(t);
  }, [editingPurchasePriceId]);

  const startEditPurchasePrice = (it) => {
    if (!onSaveExpectedPurchasePrice || savingPurchasePriceId != null || adding) return;
    setEditingBarcodeId(null);
    setBindOpenItemId(null);
    setEditingPurchasePriceId(it.id);
    const p = it.purchasePrice;
    setEditingPurchasePriceValue(
      p != null && p !== '' && Number.isFinite(Number(p)) ? String(p).replace('.', ',') : ''
    );
  };

  const commitPurchasePriceEdit = async (it) => {
    if (!onSaveExpectedPurchasePrice || editingPurchasePriceId !== it.id) return;
    try {
      await onSaveExpectedPurchasePrice(it, editingPurchasePriceValue);
      setEditingPurchasePriceId(null);
    } catch {
      /* setError у батькові */
    }
  };

  const startEditBarcode = (it) => {
    if (!onSaveExpectedBarcode || adding || savingBarcodeId != null) return;
    setEditingPurchasePriceId(null);
    setEditingBarcodeId(it.id);
    setEditingBarcodeValue(normalizeBarcodeField(it.barcode) != null ? String(it.barcode).trim() : '');
  };

  const toggleBindRow = (it) => {
    if (!onBindExpectedItem || bindingExpectedId != null || adding) return;
    const code = (it.productCode || '').trim();
    if (!code) return;
    setEditingBarcodeId(null);
    setEditingPurchasePriceId(null);
    setBindOpenItemId((prev) => {
      if (prev === it.id) return null;
      setBindQuery('');
      setBindResults([]);
      return it.id;
    });
  };

  const pickCatalogProductForBind = async (expectedItem, catalogProduct) => {
    if (!onBindExpectedItem || bindingExpectedId != null) return;
    try {
      await onBindExpectedItem(expectedItem.id, catalogProduct.id);
      setBindOpenItemId(null);
      setBindQuery('');
      setBindResults([]);
    } catch {
      /* setError у батькові */
    }
  };

  const commitBarcodeEdit = async (it) => {
    if (!onSaveExpectedBarcode || editingBarcodeId !== it.id) return;
    const nextNorm = normalizeBarcodeField(editingBarcodeValue);
    const prevNorm = normalizeBarcodeField(it.barcode);
    if (prevNorm === nextNorm) {
      setEditingBarcodeId(null);
      return;
    }
    try {
      await onSaveExpectedBarcode(it, editingBarcodeValue);
      setEditingBarcodeId(null);
    } catch {
      /* setError у батькові */
    }
  };

  const pickProduct = (p) => {
    setSelectedProduct(p);
    setProductQuery('');
    setSearchResults([]);
  };

  const clearSelection = () => {
    setSelectedProduct(null);
    setProductQuery('');
    setSearchResults([]);
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (adding || !selectedProduct) return;
    const code = selectedProduct.code || selectedProduct.productCode || null;
    const body = {
      productId: selectedProduct.id,
      barcode: selectedProduct.barcode != null && String(selectedProduct.barcode).trim() !== ''
        ? String(selectedProduct.barcode).trim()
        : null,
      productCode: code != null && String(code).trim() !== '' ? String(code).trim() : null,
      name: selectedProduct.name != null && String(selectedProduct.name).trim() !== ''
        ? String(selectedProduct.name).trim()
        : null,
      quantity: parseInventoryQuantity(quantity),
      purchasePrice: purchasePrice.trim() ? parseInventoryMoney(purchasePrice) : null,
      status: 'pending'
    };
    if (body.quantity == null || (purchasePrice.trim() && (body.purchasePrice == null || body.purchasePrice <= 0))) {
      setManualInputError('Вкажіть цілу додатну кількість та коректну ціну закупки.');
      return;
    }
    setManualInputError('');
    try {
      await onAddItem(body);
      clearSelection();
      setQuantity('1');
      setPurchasePrice('');
    } catch (err) {
      /* помилку показує батько */
    }
  };

  return (
    <div className='card mb-3' data-incoming-expected-block='true'>
      <div
        className='card-header d-flex align-items-center flex-wrap py-2'
        style={{ cursor: 'pointer', gap: '0.35rem 0.5rem' }}
        onClick={() => setOpen(!open)}
        role='button'
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(!open);
          }
        }}
      >
        <div className='d-flex align-items-center flex-grow-1 flex-wrap' style={{ minWidth: 0 }}>
          <span
            className='mr-2 font-weight-bold text-dark flex-shrink-0'
            style={{ fontSize: '1.25rem', lineHeight: 1, userSelect: 'none' }}
            aria-hidden
          >
            {open ? '−' : '+'}
          </span>
          <span className='font-weight-bold mb-0'>Очікувані товари</span>
          <span className='text-muted small ml-2'>({(items || []).length})</span>
          <div
            className='form-check form-check-inline mb-0 ml-3'
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            role='presentation'
          >
            <input
              className='form-check-input'
              type='checkbox'
              id='incoming-expected-show-purchase-price'
              checked={showPurchasePriceColumn}
              onChange={(e) => setShowPurchasePriceColumn(e.currentTarget.checked)}
              style={{
                appearance: 'auto',
                WebkitAppearance: 'auto',
                width: '1.05rem',
                height: '1.05rem',
                marginTop: '0.1rem'
              }}
            />
            <label
              className='form-check-label small text-muted'
              htmlFor='incoming-expected-show-purchase-price'
              style={{ cursor: 'pointer', userSelect: 'none', marginLeft: '0.25rem' }}
            >
              Показувати ціну закупки
            </label>
          </div>
        </div>
        {showGenerateFromScannedBtn ? (
          <button
            type='button'
            className='btn btn-sm btn-info flex-shrink-0 ml-md-auto'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void onGenerateExpectedFromScanned();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={Boolean(
              generatingExpectedFromScanned ||
                adding ||
                deletingId != null ||
                incomingToolbarBusy
            )}
            title='Створює рядки з просканованих (ціна закупки додається вручну)'
          >
            {generatingExpectedFromScanned ? '…' : 'Заповнити з просканованих'}
          </button>
        ) : null}
      </div>
      {open ? (
        <div className='card-body py-3'>

          <div className='mb-3' data-incoming-expected-block='true'>
            <div
              className='d-flex align-items-center flex-wrap'
            >
              <div
                className='d-flex align-items-center py-1'
                style={{ cursor: 'pointer', minWidth: 0 }}
                onClick={() => setManualAddOpen(!manualAddOpen)}
                role='button'
                tabIndex={0}
                aria-expanded={manualAddOpen}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setManualAddOpen(!manualAddOpen);
                  }
                }}
              >
                <span
                  className='mr-2 font-weight-bold text-dark flex-shrink-0'
                  style={{ fontSize: '1.25rem', lineHeight: 1, userSelect: 'none' }}
                  aria-hidden
                >
                  {manualAddOpen ? '−' : '+'}
                </span>
                <span className='font-weight-bold small mb-0'>1. Додати вручну</span>
              </div>
              {onOpenExcelImport ? (
                <button
                  type='button'
                  className='btn btn-sm btn-dark flex-shrink-0 ml-2 px-2 py-1'
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenExcelImport();
                  }}
                  disabled={adding}
                  data-incoming-expected-block='true'
                >
                  2. Імпорт з файлу
                </button>
              ) : null}
            </div>

            {manualAddOpen ? (
              <>
              {manualInputError && <div className='alert alert-danger' role='alert'>{manualInputError}</div>}
              <form className='mt-2 mb-0' onSubmit={submitAdd} data-incoming-expected-block='true'>
                <div
                  className='form-row align-items-center flex-md-nowrap'
                  data-incoming-expected-block='true'
                  data-expected-product-search='true'
                  style={{ overflow: 'visible' }}
                >
                  <div className='col-12 col-md mb-2 mb-md-0 position-relative' style={{ minWidth: 0, zIndex: 4 }}>
                    {selectedProduct ? (
                      <div className='d-flex align-items-center' style={{ gap: '0.35rem' }}>
                        <span className='text-truncate flex-grow-1' title={productLine(selectedProduct)}>
                          {productLine(selectedProduct)}
                        </span>
                        <button
                          type='button'
                          className='btn btn-sm btn-outline-secondary flex-shrink-0'
                          onClick={clearSelection}
                          disabled={adding}
                        >
                          Змінити
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className='position-relative'>
                          <input
                            type='text'
                            className='form-control form-control-sm'
                            placeholder='Товар: назва, артикул, штрихкод… (мін. 2 симв.)'
                            title='Введіть щонайменше 2 символи для пошуку в каталозі'
                            value={productQuery}
                            onChange={(e) => setProductQuery(e.target.value)}
                            disabled={adding}
                            autoComplete='off'
                          />
                          {searchLoading ? (
                            <span
                              className='position-absolute text-muted small'
                              style={{ right: '0.5rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                            >
                              …
                            </span>
                          ) : null}
                        </div>
                        {!selectedProduct && (searchResults.length > 0 || (!searchLoading && productQuery.trim().length >= 2)) ? (
                          <div
                            className='position-absolute border rounded bg-white shadow-sm'
                            style={{
                              left: 0,
                              right: 0,
                              top: '100%',
                              marginTop: 2,
                              maxHeight: 200,
                              overflowY: 'auto',
                              zIndex: 20
                            }}
                            data-incoming-expected-block='true'
                          >
                            {!searchLoading && productQuery.trim().length >= 2 && searchResults.length === 0 ? (
                              <div className='px-2 py-2 small text-muted'>Нічого не знайдено</div>
                            ) : null}
                            {searchResults.slice(0, 12).map((p) => (
                              <button
                                key={p.id}
                                type='button'
                                className='dropdown-item px-2 py-2 text-left w-100 border-0 bg-transparent'
                                style={{ whiteSpace: 'normal' }}
                                onClick={() => pickProduct(p)}
                                disabled={adding}
                              >
                                <span className='font-weight-bold'>{p.code || p.productCode || `#${p.id}`}</span>
                                {' — '}
                                <span>{p.name}</span>
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                  <div className='col-4 col-md-auto mb-2 mb-md-0 pl-md-2' style={{ width: 'auto', maxWidth: '5.5rem' }}>
                    <input
                      className='form-control form-control-sm'
                      type='number'
                      min={1}
                      placeholder='К-ть'
                      title='Кількість'
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      disabled={adding}
                    />
                  </div>
                  <div className='col-8 col-md-auto mb-2 mb-md-0 pl-md-2' style={{ width: 'auto', minWidth: '6.5rem', maxWidth: '9rem' }}>
                    <input
                      className='form-control form-control-sm'
                      placeholder='Ціна закупки'
                      title='Ціна закупки (необовʼязково)'
                      value={purchasePrice}
                      onChange={(e) => setPurchasePrice(e.target.value)}
                      disabled={adding}
                    />
                  </div>
                  <div className='col-12 col-md-auto pl-md-2'>
                    <button
                      type='submit'
                      className='btn btn-sm btn-dark'
                      style={{ minWidth: '5.5rem' }}
                      disabled={adding || !selectedProduct}
                    >
                      {adding ? '…' : 'Додати'}
                    </button>
                  </div>
                </div>
              </form>
              </>
            ) : null}
          </div>

          <div className='d-flex align-items-center justify-content-start mt-5 mb-1'>
            <div className='text-muted' style={{ fontSize: '1.05rem' }}>
              Загальна сума:{' '}
              <span className='font-weight-bold text-dark'>{fmtMoneyUah(totalPurchaseSum)}</span>
            </div>
          </div>

          <div className='table-responsive incoming-expected-table-wrap'>
            <table className='table table-sm mb-0'>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Артикул</th>
                  <th title={onSaveExpectedBarcode ? 'Подвійний клік по комірці — змінити штрихкод' : undefined}>Штрихкод</th>
                  <th>Назва</th>
                  <th>Кількість</th>
                  {showPurchasePriceColumn ? <th>Ціна закупки</th> : null}
                  {showPurchasePriceColumn ? <th className='incoming-expected-margin-column' title='(Роздрібна ціна − ціна закупки) / роздрібна ціна × 100%. Роздрібна ціна без знижки, за одиницю.'>Маржа</th> : null}
                  <th>Статус</th>
                  <th className='text-right' />
                </tr>
              </thead>
              <tbody>
                {(items || []).flatMap((it) => {
                  const scannedQty = getScannedQtyForExpected(it);
                  const expQty = Math.max(0, parseInt(it?.quantity, 10) || 0);
                  const scanClass =
                    expQty > 0 && scannedQty > 0
                      ? (scannedQty > expQty ? 'table-danger' : (scannedQty === expQty ? 'table-success' : 'table-warning'))
                      : '';
                  const canBind =
                    onBindExpectedItem &&
                    it.productId == null &&
                    (it.productCode || '').trim() !== '';
                  const bindPanelOpen = bindOpenItemId === it.id;
                  const mainRow = (
                  <tr key={it.id} className={scanClass} title={expQty > 0 ? `Проскановано ${scannedQty} / ${expQty}` : undefined}>
                    <td className='text-monospace'>{it.productId != null ? it.productId : '—'}</td>
                    <td className='text-monospace'>{it.productCode || '—'}</td>
                    <td
                      className={`text-monospace ${onSaveExpectedBarcode && !adding ? 'cursor-pointer' : ''}`}
                      title={onSaveExpectedBarcode ? 'Подвійний клік — змінити штрихкод' : undefined}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (savingBarcodeId != null) return;
                        startEditBarcode(it);
                      }}
                    >
                      {editingBarcodeId === it.id ? (
                        <input
                          ref={barcodeInputRef}
                          type='text'
                          className='form-control form-control-sm text-monospace py-0'
                          style={{ minWidth: '7rem' }}
                          value={editingBarcodeValue}
                          onChange={(e) => setEditingBarcodeValue(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void commitBarcodeEdit(it);
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              setEditingBarcodeId(null);
                            }
                          }}
                          onBlur={() => {
                            window.setTimeout(() => {
                              void commitBarcodeEdit(it);
                            }, 0);
                          }}
                          disabled={savingBarcodeId === it.id}
                          autoComplete='off'
                        />
                      ) : savingBarcodeId === it.id ? (
                        <span className='text-muted'>…</span>
                      ) : (
                        it.barcode || '—'
                      )}
                    </td>
                    <td>
                      <span className='align-middle'>{it.name || '—'}</span>
                      {canBind ? (
                        <button
                          type='button'
                          className='btn btn-link btn-sm p-0 ml-1 align-baseline text-primary'
                          title={
                            bindPanelOpen
                              ? 'Згорнути пошук товару в каталозі'
                              : 'Знайти товар у каталозі за назвою (артикул з рядка запишеться в товар)'
                          }
                          aria-label={bindPanelOpen ? 'Згорнути привʼязку до каталогу' : 'Привʼязати до товару в каталозі'}
                          disabled={
                            adding ||
                            deletingId === it.id ||
                            bindingExpectedId != null ||
                            savingBarcodeId != null
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleBindRow(it);
                          }}
                          data-incoming-expected-block='true'
                        >
                          <i className={`bi ${bindPanelOpen ? 'bi-chevron-up' : 'bi-link-45deg'}`} aria-hidden />
                        </button>
                      ) : null}
                    </td>
                    <td>
                      {expQty > 0 && scannedQty > 0 ? (
                        <span className={scannedQty > expQty ? 'font-weight-bold text-danger' : 'font-weight-bold'}>
                          {scannedQty}/{expQty}
                        </span>
                      ) : (
                        it.quantity
                      )}
                    </td>
                    {showPurchasePriceColumn ? (
                      <td
                        className={onSaveExpectedPurchasePrice && editingPurchasePriceId !== it.id ? 'cursor-pointer' : ''}
                        title={
                          onSaveExpectedPurchasePrice ? 'Подвійний клік — змінити ціну закупки' : undefined
                        }
                        onDoubleClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (savingPurchasePriceId != null || it.status === 'posted') return;
                          startEditPurchasePrice(it);
                        }}
                      >
                        {onSaveExpectedPurchasePrice && editingPurchasePriceId === it.id ? (
                          <input
                            ref={purchasePriceInputRef}
                            type='text'
                            inputMode='decimal'
                            className='form-control form-control-sm py-0'
                            style={{ minWidth: '5rem', maxWidth: '8rem' }}
                            placeholder='0,00'
                            value={editingPurchasePriceValue}
                            onChange={(e) => setEditingPurchasePriceValue(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                void commitPurchasePriceEdit(it);
                              }
                              if (e.key === 'Escape') {
                                e.preventDefault();
                                setEditingPurchasePriceId(null);
                              }
                            }}
                            onBlur={() => {
                              window.setTimeout(() => {
                                void commitPurchasePriceEdit(it);
                              }, 0);
                            }}
                            disabled={savingPurchasePriceId === it.id}
                            autoComplete='off'
                          />
                        ) : savingPurchasePriceId === it.id ? (
                          <span className='text-muted'>…</span>
                        ) : (
                          fmtPrice(it.purchasePrice)
                        )}
                      </td>
                    ) : null}
                    {showPurchasePriceColumn ? <td className='text-nowrap' data-label='Маржа' title=''>
                      <IncomingItemMargin retailPrice={it.currentSellingPrice}
                        purchasePrice={editingPurchasePriceId === it.id ? editingPurchasePriceValue : it.purchasePrice}
                        tooltipId={`incoming-margin-${it.id}`} />
                    </td> : null}
                    <td>
                      {onChangeExpectedItemStatus ? (
                        <IncomingStatusBadgePicker
                          value={it.status || 'pending'}
                          options={expectedStatusOptionsFor(it.status)}
                          labelOf={expectedItemStatusLabel}
                          badgeClassOf={expectedItemStatusBadgeClass}
                          onPick={(next) => onChangeExpectedItemStatus(it.id, next)}
                          disabled={
                            it.status === 'posted' ||
                            savingExpectedStatusId === it.id ||
                            deletingId === it.id ||
                            adding ||
                            bindingExpectedId != null ||
                            savingBarcodeId != null
                          }
                          title='Статус позиції — натисніть, щоб змінити'
                        />
                      ) : (
                        <span className={expectedItemStatusBadgeClass(it.status)} title={it.status || ''}>
                          {expectedItemStatusLabel(it.status)}
                        </span>
                      )}
                    </td>
                    <td className='text-right text-nowrap' data-incoming-expected-block='true'>
                      {onPostExpectedToSite &&
                      it.status === 'scanned_full' &&
                      it.productId != null ? (
                        <button
                          type='button'
                          className='btn btn-link btn-sm align-baseline'
                          title={
                            expectedItemHasValidPurchasePrice(it)
                              ? 'Внести надходження'
                              : 'Спочатку вкажіть ціну закупки'
                          }
                          style={{ padding: '2px 4px', color: '#000' }}
                          disabled={
                            !expectedItemHasValidPurchasePrice(it) ||
                            postingExpectedToSiteId === it.id ||
                            deletingId === it.id ||
                            adding ||
                            bindingExpectedId != null ||
                            savingBarcodeId != null ||
                            it.status === 'posted' ||
                            savingExpectedStatusId === it.id ||
                            savingPurchasePriceId === it.id
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            void onPostExpectedToSite(it.id);
                          }}
                          data-incoming-expected-block='true'
                        >
                          <i className='bi bi-box-seam' style={{ fontSize: '1.125rem' }} aria-hidden />
                        </button>
                        ) : null}
                      <button
                        type='button'
                        className='btn btn-sm btn-outline-danger'
                        title='Видалити'
                        disabled={deletingId === it.id || adding || Boolean(expectedDeleteConfirmOpen)}
                        onClick={() => onDeleteItem(it.id)}
                      >
                        <i className='bi bi-trash' aria-hidden />
                      </button>
                    </td>
                  </tr>
                  );
                  if (!bindPanelOpen) return [mainRow];
                  return [
                    mainRow,
                    <tr key={`${it.id}-bind`} className='table-light'>
                      <td colSpan={columnCount} className='p-2 border-top' data-incoming-expected-block='true'>
                        <div className='small text-muted mb-2'>
                          Артикул <span className='text-monospace font-weight-bold'>{it.productCode}</span> з накладної буде
                          записано в обраний товар; у таблиці зʼявляться ID та штрихкод з каталогу. Назва з файлу лишається без змін.
                        </div>
                        <div className='position-relative' style={{ maxWidth: '36rem' }}>
                          <input
                            ref={bindInputRef}
                            type='text'
                            className='form-control form-control-sm'
                            placeholder='Введіть назву для пошуку в каталозі (мін. 2 симв.)'
                            value={bindQuery}
                            onChange={(e) => setBindQuery(e.target.value)}
                            disabled={bindingExpectedId === it.id}
                            autoComplete='off'
                          />
                          {bindLoading ? (
                            <span
                              className='position-absolute text-muted small'
                              style={{ right: '0.5rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                            >
                              …
                            </span>
                          ) : null}
                          {!bindLoading && bindQuery.trim().length >= 2 && bindResults.length === 0 ? (
                            <div className='small text-muted mt-1'>Нічого не знайдено</div>
                          ) : null}
                          {bindResults.length > 0 ? (
                            <div
                              className='position-absolute border rounded bg-white shadow-sm w-100'
                              style={{
                                left: 0,
                                top: '100%',
                                marginTop: 2,
                                maxHeight: 220,
                                overflowY: 'auto',
                                zIndex: 25
                              }}
                            >
                              {bindResults.slice(0, 14).map((p) => (
                                <button
                                  key={p.id}
                                  type='button'
                                  className='dropdown-item px-2 py-2 text-left w-100 border-0 bg-transparent'
                                  style={{ whiteSpace: 'normal' }}
                                  disabled={bindingExpectedId === it.id}
                                  onClick={() => pickCatalogProductForBind(it, p)}
                                >
                                  <span className='font-weight-bold'>{p.code || p.productCode || `#${p.id}`}</span>
                                  {' — '}
                                  <span>{p.name}</span>
                                </button>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        <button
                          type='button'
                          className='btn btn-sm btn-link px-0 mt-1'
                          disabled={bindingExpectedId === it.id}
                          onClick={() => {
                            setBindOpenItemId(null);
                            setBindQuery('');
                            setBindResults([]);
                          }}
                        >
                          Скасувати
                        </button>
                        {bindingExpectedId === it.id ? (
                          <span className='text-muted small ml-2'>Збереження…</span>
                        ) : null}
                      </td>
                    </tr>
                  ];
                })}
                {!(items || []).length ? (
                  <tr>
                    <td colSpan={columnCount} className='text-muted'>Очікуваних позицій ще немає</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default IncomingShipmentExpectedBlock;
